import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const getAnchorPosition = (rect, origin) => {
  const vertical = origin?.vertical ?? 'bottom';
  const horizontal = origin?.horizontal ?? 'left';

  const top =
    vertical === 'top'
      ? rect.top
      : vertical === 'center'
        ? rect.top + rect.height / 2
        : rect.bottom;

  const left =
    horizontal === 'left'
      ? rect.left
      : horizontal === 'center'
        ? rect.left + rect.width / 2
        : rect.right;

  return { top, left };
};

const getTransformOffset = (size, origin) => {
  const vertical = origin?.vertical ?? 'top';
  const horizontal = origin?.horizontal ?? 'left';

  const offsetY =
    vertical === 'top'
      ? 0
      : vertical === 'center'
        ? size.height / 2
        : size.height;
  const offsetX =
    horizontal === 'left'
      ? 0
      : horizontal === 'center'
        ? size.width / 2
        : size.width;

  return { offsetX, offsetY };
};

export function Popover({
  open,
  anchorEl = null,
  anchorOrigin = null,
  transformOrigin = null,
  onClose,
  modal = false,
  showBackdrop = false,
  className = '',
  children,
}) {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  // Helper function to calculate position
  const calculatePosition = () => {
    if (!open || modal || !anchorEl || !popoverRef.current) return;
    const rect = anchorEl.getBoundingClientRect();
    const size = popoverRef.current.getBoundingClientRect();

    const anchorPos = getAnchorPosition(rect, anchorOrigin);
    const transformOffset = getTransformOffset(size, transformOrigin);

    // Use viewport-relative coordinates for fixed positioning
    let top = anchorPos.top - transformOffset.offsetY;
    let left = anchorPos.left - transformOffset.offsetX;

    // Keep popover within viewport bounds
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 8;

    // Adjust horizontal position
    if (left + size.width > viewportWidth - margin) {
      left = viewportWidth - size.width - margin;
    }
    if (left < margin) {
      left = margin;
    }

    // Adjust vertical position
    if (top + size.height > viewportHeight - margin) {
      top = viewportHeight - size.height - margin;
    }
    if (top < margin) {
      top = margin;
    }

    setPosition({ top, left });
  };

  useLayoutEffect(() => {
    calculatePosition();
  }, [open, anchorEl, anchorOrigin, transformOrigin]);

  // Add scroll listener to update position when page scrolls
  useEffect(() => {
    if (!open || modal) return;

    window.addEventListener('scroll', calculatePosition, { passive: true });
    return () => window.removeEventListener('scroll', calculatePosition);
  }, [open, modal, anchorEl, anchorOrigin, transformOrigin]);

  // Prevent page scrolling when popover is open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open || (modal && showBackdrop)) return;
    const handleClick = (event) => {
      const clickedCalendar = event.target?.closest?.('.flatpickr-calendar');
      if (clickedCalendar) return;
      const clickedInsidePopover = popoverRef.current?.contains(event.target);
      const clickedAnchor = anchorEl?.contains?.(event.target);
      if (!clickedInsidePopover && !clickedAnchor) {
        onClose?.();
      }
    };
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, anchorEl, modal, showBackdrop, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={`popover${modal ? ' popover--modal' : ''}${
        showBackdrop ? ' popover--backdrop' : ''
      }${className ? ` ${className}` : ''}`}
    >
      {showBackdrop && <div className="popover__backdrop" onClick={onClose} />}
      <div
        ref={popoverRef}
        className={`popover__panel${modal ? ' popover__panel--modal' : ''}`}
        style={modal ? undefined : { top: position.top, left: position.left }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
